import { cn } from '@/lib/utils';
import { Info, ArrowLeft } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { useShallow } from 'zustand/react/shallow';
import { useHeaderStore } from '@/store/useHeaderStore';
import { useNavigate } from 'react-router-dom';
import { useNavCompact } from '@/hooks/use-nav-compact';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const { t } = useTranslation();
  const { text, subtitle, subtitleTooltipClassName, children, className, showBack, backTo, onBack } = useHeaderStore(
    useShallow((state) => ({
      text: state.text,
      subtitle: state.subtitle,
      subtitleTooltipClassName: state.subtitleTooltipClassName,
      children: state.children,
      className: state.className,
      showBack: state.showBack,
      backTo: state.backTo,
      onBack: state.onBack,
    }))
  );
  const navigate = useNavigate();
  const isNavCompact = useNavCompact();

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (backTo) {
      navigate(backTo);
    }
  };

  if (!text) return null;

  const isReactNode = typeof text !== 'string';
  const displayText = isReactNode ? text : capitalizeFirstLetter(text);

  return (
    <div
      className={cn(
        'flex justify-between gap-3 py-4 nav:py-3.5 border-b border-gray-200 px-4 sm:px-6 nav:px-10 min-w-0',
        className
      )}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1 min-h-[36px]">
        {isNavCompact && (
          <SidebarTrigger className="size-10 min-h-10 min-w-10 shrink-0 text-gray-700 hover:bg-gray-100" />
        )}
        {showBack && (
          <button
            type="button"
            onClick={handleBack}
            aria-label={t('common.back')}
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-transparent dark:hover:bg-gray-800"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        <h1 className={cn('font-semibold truncate text-lg sm:text-xl nav:text-[22px]')}>{displayText}</h1>

        {subtitle && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="cursor-help shrink-0">
                <Info className="w-4 cursor-help" />
              </div>
            </TooltipTrigger>
            <TooltipContent className={cn('max-w-sm whitespace-normal', subtitleTooltipClassName)}>
              <p>{subtitle}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  );
};

export default Header;
